// The ranked artwork list the /bestsellers grid is currently paging, if any.
//
// Two of the four tabs rank by something no `ORDER BY` can express — copies sold per
// reseller, and a clustered multi-image kNN — so a mutation computes the whole ranking once
// and the grid pages it. This module holds that list between pages.
//
// ## Where it joins the grid
//
// `lm_filters` is built ONCE when a search starts and re-read for every page, so the page's
// own slice cannot be put there. It is injected at the single request site in
// `ui/contentLoader.ts` instead, which is also the only place that knows `from` and `qty`.
//
// ⚠️ A store rather than a hidden input, for the same reason `searchImage/store.ts` is one:
// this is up to 3,000 numbers, and pushing it through a string input would mean serialising
// on every write and parsing on every read with nothing checking the shape at either end.

/** The active ranking, or null when the grid is paging normally. */
let ranking: number[] | null = null;

/**
 * Begin paging a ranking, or stop (`null`).
 *
 * The caller resets `lm_from` and re-runs the grid; this only holds the list.
 */
export function setRanking(ids: number[] | null): void {
  ranking = ids;
}

/** True when a ranking is active — including an EMPTY one, which is a real answer. */
export function hasRanking(): boolean {
  return ranking !== null;
}

/** How many artworks the ranking holds. 0 is "this reseller has nothing to show". */
export function rankingSize(): number {
  return ranking?.length ?? 0;
}

/**
 * The ids for one page, as the comma string `search.load` takes in `rankedids`.
 *
 * Returns null when no ranking is active, and an EMPTY STRING when the ranking is active but
 * this page is past its end. The two must not be confused: an absent `rankedids` tells the
 * server to page normally, so sending nothing at the end of a top-sellers list would fill
 * the last page with arbitrary artworks that never sold. The caller stops instead.
 */
export function rankedSlice(from: number, qty: number): string | null {
  if (ranking === null) return null;
  return ranking.slice(from, from + qty).join(",");
}

/**
 * Fold this page's slice into a `lm_filters` payload.
 *
 * Returns the payload UNTOUCHED when no ranking is active, so every page everywhere else on
 * the site sends exactly the bytes it did before this existed. Returns null when the ranking
 * is exhausted, which the caller reads as "do not request".
 */
export function withRankedIds(filtersJson: string, from: number, qty: number): string | null {
  const slice = rankedSlice(from, qty);
  if (slice === null) return filtersJson;
  if (slice === "") return null;
  try {
    const parsed: unknown = JSON.parse(filtersJson);
    // The payload is an array with one filter object in it — the shape every call site in
    // `navigation.ts` builds. Anything else is left alone rather than guessed at.
    if (!Array.isArray(parsed) || typeof parsed[0] !== "object" || parsed[0] === null) {
      return filtersJson;
    }
    // Each narrowed to `unknown` rather than left as the `any` a JSON parse produces: the
    // tail is passed straight through, so it needs no shape, but spreading `any[]` is how an
    // unchecked value gets back into typed code.
    const [first, ...rest] = parsed as unknown[];
    return JSON.stringify([{ ...(first as Record<string, unknown>), rankedids: slice }, ...rest]);
  } catch {
    // A malformed payload is not this module's problem to report; the grid will fail the
    // same way it would have without a ranking.
    return filtersJson;
  }
}
